import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Select, InputLabel, FormControl, Typography, Box, Checkbox } from '@mui/material';
import { enhancedApi } from '../api';

const AddTeamForm = ({ open, onClose, onTeamSubmit, teamToEdit }) => {
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [iconColor, setIconColor] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');

  const isEditMode = !!teamToEdit;  // Determine if we're in edit mode

  const colorOptions = [
    { label: 'Red', value: '#FF0000' },
    { label: 'Blue', value: '#0000FF' },
    { label: 'Green', value: '#008000' },
    { label: 'Yellow', value: '#FFFF00' },
    { label: 'Purple', value: '#800080' },
    { label: 'Orange', value: '#FFA500' },
  ];

  // Fetch users to populate the dropdown
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await enhancedApi.getUsers();
      const data = response.data;
      const userList = Array.isArray(data) ? data : (data?.results || []);
      setUsers(userList);
      setError('');
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    }
  };

  // Pre-fill form data when in edit mode (users from API are usernames)
  useEffect(() => {
    if (open && isEditMode && teamToEdit) {
      setTeamName(teamToEdit.name || '');
      setDescription(teamToEdit.description || '');
      setIconColor(teamToEdit.icon_color || '');
      setSelectedUsers(Array.isArray(teamToEdit.users) ? teamToEdit.users : []);
    } else if (open) {
      // Reset form for create mode
      setTeamName('');
      setDescription('');
      setIconColor('');
      setSelectedUsers([]);
      setError('');
    }
  }, [open, isEditMode, teamToEdit]);

  const handleSubmit = async () => {
    if (!teamName) {
      setError('Team name is required');
      return;
    }

    const teamData = {
      name: teamName,
      description,
      icon_color: iconColor,
      users: selectedUsers,
    };

    try {
      if (isEditMode) {
        const res = await enhancedApi.updateTeam(teamToEdit.id, teamData);
        if (res.data?.error) throw new Error(res.data.error);
      } else {
        const res = await enhancedApi.createTeam(teamData);
        if (res.data?.success === false && res.data?.error) throw new Error(res.data.error);
      }
      onTeamSubmit();
      onClose();
    } catch (error) {
      console.error('Error saving team:', error);
      const errMsg = error.response?.data?.error || (typeof error.response?.data?.error === 'object' ? JSON.stringify(error.response.data.error) : null) || error.message || 'Failed to save team';
      setError(errMsg);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? 'Edit Team' : 'Create New Team'}</DialogTitle>
      <DialogContent>
        {error && <Typography color="error">{error}</Typography>}
        <TextField
          label="Team Name"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={3}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Team Icon Color</InputLabel>
          <Select
            value={iconColor}
            onChange={(e) => setIconColor(e.target.value)}
            label="Team Icon Color"
          >
            <MenuItem value="">None</MenuItem>
            {colorOptions.map(color => (
              <MenuItem key={color.value} value={color.value}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 20, height: 20, backgroundColor: color.value, mr: 1 }} />
                  {color.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Assign Users</InputLabel>
          <Select
            multiple
            value={selectedUsers}
            onChange={(e) => setSelectedUsers(e.target.value)}
            label="Assign Users"
            renderValue={(selected) => selected.join(', ')}
          >
            {users.map(user => (
              <MenuItem key={user.id} value={user.username}>
                <Checkbox checked={selectedUsers.includes(user.username)} />
                <Typography>{user.username}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {isEditMode ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeamForm;